
import { ethers } from 'ethers';
import contractData from '../contracts/SmallClaimsCaseRegistry.json';

// Network constants
export const BASE_MAINNET = {
  chainId: process.env.REACT_APP_BASE_CHAIN_ID ? Number(process.env.REACT_APP_BASE_CHAIN_ID) : 8453,
  name: "Base Mainnet",
  rpcUrl: process.env.REACT_APP_BASE_RPC_URL || "https://mainnet.base.org",
  blockExplorer: "https://basescan.org",
  symbol: "ETH",
  decimals: 18
};

// Contract addresses
export const CONTRACT_ADDRESSES = {
  SmallClaimsCaseRegistry: process.env.REACT_APP_BASE_CONTRACT_ADDRESS || "0x4B2cF77Dbd063299536AB916Fb1445bD3267162F",
  registry: process.env.REACT_APP_BASE_CONTRACT_ADDRESS || "0x4B2cF77Dbd063299536AB916Fb1445bD3267162F", // Alias for SmallClaimsCaseRegistry
  // If you have other contracts, add them here
};

/**
 * Format an Ethereum address for display (0x1234...5678)
 * @param {string} address - The full address to format
 * @param {number} [chars=4] - Number of characters to show at start and end
 * @returns {string} The formatted address
 */
export function formatAddress(address, chars = 4) {
  if (!address) return '';
  
  // Ensure we're working with a string
  const addressStr = address.toString();
  return `${addressStr.slice(0, chars + 2)}...${addressStr.slice(-chars)}`;
}

/**
 * Get a user-friendly network name from a chain ID
 * @param {number} chainId - The chain ID to get the name for
 * @returns {string} The network name
 */
export function getNetworkName(chainId) {
  switch (chainId) {
    case 1:
      return 'Ethereum Mainnet';
    case 3:
      return 'Ropsten Testnet';
    case 4:
      return 'Rinkeby Testnet';
    case 5:
      return 'Goerli Testnet';
    case 42:
      return 'Kovan Testnet';
    case 56:
      return 'Binance Smart Chain';
    case 137:
      return 'Polygon Mainnet';
    case 42161:
      return 'Arbitrum One';
    case 10:
      return 'Optimism';
    case 43114:
      return 'Avalanche C-Chain';
    case 8453:
      return 'Base Mainnet';
    case 84531:
      return 'Base Goerli';
    case 84532:
      return 'Base Sepolia';
    default:
      return `Unknown Network (${chainId})`;
  }
}

/**
 * Get a provider for the Base network
 * @returns {ethers.Provider} An ethers provider
 */
export function getProvider() {
  // Check if MetaMask or another injected provider is available
  if (window.ethereum) {
    // Ensure we're connected to the correct network
    // Base Mainnet ChainId = 8453
    return new ethers.BrowserProvider(window.ethereum);
  }
  
  // Fallback to a JSON RPC provider if no injected provider is available
  // Use RPC endpoints with API key for reliability
  return new ethers.JsonRpcProvider(BASE_MAINNET.rpcUrl);
}

/**
 * Get contract instance
 * @param {ethers.Signer} [signer] - Optional signer for transactions
 * @returns {ethers.Contract} An ethers Contract instance
 */
export function getContract(signer) {
  const provider = getProvider();
  const { address, abi } = contractData;
  
  if (signer) {
    return new ethers.Contract(address, abi, signer);
  }
  
  return new ethers.Contract(address, abi, provider);
}

/**
 * Check if an account has a specific role
 * @param {string} account - The account address to check
 * @param {string} role - The role to check (e.g. 'REGISTRAR_ROLE')
 * @returns {Promise<boolean>} Whether the account has the role
 */
export async function hasRole(account, role) {
  try {
    console.log(`Checking if account ${account} has role ${role}`);
    
    // Ensure we have the most current provider and contract instance
    if (window.ethereum) {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(
        CONTRACT_ADDRESSES.registry,
        contractData.abi,
        signer
      );
      
      // Get the role hash
      const roleHash = contractData.roles[role] || role;
      console.log(`Using role hash: ${roleHash}`);
      
      // Convert address to checksum address to ensure correct format
      const checksumAddress = ethers.getAddress(account);
      console.log(`Checking role for checksum address: ${checksumAddress}`);
      
      // Call the contract method
      const hasRoleResult = await contract.hasRole(roleHash, checksumAddress);
      console.log(`Role check result for ${checksumAddress}: ${hasRoleResult}`);
      
      return hasRoleResult;
    } else {
      // Fallback to readonly provider if no ethereum object
      const contract = getContract();
      const roleHash = contractData.roles[role] || role;
      const checksumAddress = ethers.getAddress(account);
      return await contract.hasRole(roleHash, checksumAddress);
    }
  } catch (error) {
    console.error(`Error checking if account ${account} has role ${role}:`, error);
    return false; // Return false on error to be safe
  }
}

/**
 * Register a new case
 * @param {string} plaintiff - The plaintiff's address
 * @param {string} ipfsUri - The IPFS URI for the case data
 * @param {string} initialStatus - The initial status of the case
 * @param {string} defendant - The defendant's address (or zero address)
 * @param {Object} [options] - Transaction options like value
 * @returns {Promise<{transactionHash: string, tokenId: number, receipt: object}>} The transaction details
 */
export async function registerCase(plaintiff, ipfsUri, initialStatus, defendant, options = {}) {
  try {
    console.log("Starting case registration...");
    
    if (!window.ethereum) {
      throw new Error("No Ethereum provider found. Please install MetaMask.");
    }
    
    // Create a provider and signer
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    
    // Create contract instance with the signer
    const contract = new ethers.Contract(
      CONTRACT_ADDRESSES.registry,
      contractData.abi,
      signer
    );
    
    // Get filing fee if not specified
    let filingFee = options.value;
    if (!filingFee) {
      filingFee = await contract.filingFee();
    }
    console.log(`Filing fee: ${ethers.formatEther(filingFee)} ETH`);
    
    console.log("Sending transaction with params:", {
      plaintiff,
      ipfsUri,
      initialStatus,
      defendant,
      value: filingFee.toString()
    });
    
    // Send transaction
    const tx = await contract.registerCase(
      plaintiff,
      ipfsUri,
      initialStatus,
      defendant,
      { value: filingFee }
    );
    
    console.log(`Transaction sent: ${tx.hash}`);
    
    // Wait for confirmation
    console.log("Waiting for transaction confirmation...");
    const receipt = await tx.wait();
    console.log("Transaction confirmed:", receipt);
    
    // Extract case ID from events - simplified parsing method
    let tokenId = 0; // Default value
    if (receipt.logs && receipt.logs.length > 0) {
      console.log("Processing logs...");
      for (const log of receipt.logs) {
        console.log("Log:", log);
        if (log.topics && log.topics.length >= 4) {
          try {
            tokenId = parseInt(log.topics[3], 16);
            console.log(`Found case ID: ${tokenId}`);
            break;
          } catch (e) {
            console.error("Error parsing log topic:", e);
            continue;
          }
        }
      }
    }
    
    return {
      transactionHash: tx.hash,
      tokenId: tokenId,
      receipt: receipt
    };
  } catch (error) {
    console.error("Error in registerCase:", error);
    throw error;
  }
}

/**
 * Update the status of an existing case
 * @param {number} caseId - The ID of the case to update
 * @param {string} newStatus - The new status for the case
 * @param {string} ipfsUri - The updated IPFS URI for the case metadata
 * @param {ethers.Signer} signer - The signer to use for the transaction
 * @returns {Promise<{transactionHash: string}>} The transaction hash
 */
export async function updateCaseStatus(caseId, newStatus, ipfsUri, signer) {
  try {
    console.log(`Updating case status for case ID ${caseId}...`);
    const contract = getContract(signer);
    
    // Get verification fee from contract
    const verificationFee = await contract.verificationFee();
    console.log(`Verification fee: ${ethers.formatEther(verificationFee)} ETH`);
    
    // Send transaction
    const tx = await contract.updateCaseStatus(
      caseId,
      newStatus,
      ipfsUri,
      { value: verificationFee }
    );
    
    console.log(`Transaction sent: ${tx.hash}`);
    
    // Wait for confirmation
    await tx.wait();
    
    return {
      transactionHash: tx.hash
    };
  } catch (error) {
    console.error(`Error updating case status for case ID ${caseId}:`, error);
    throw error;
  }
}

/**
 * Mint a digital artifact related to a case
 * This is a placeholder function for the DigitalArtifactCreator component
 */
export async function mintDigitalArtifact(recipient, tokenUri, metadata, signer) {
  try {
    // This is a mock implementation - actual implementation would depend on your artifact contract
    console.log("Minting digital artifact...");
    console.log("Parameters:", { recipient, tokenUri, metadata });
    
    // Return mock data for now
    return {
      transactionHash: "0x" + Math.random().toString(16).substr(2, 64),
      tokenId: Math.floor(Math.random() * 1000)
    };
  } catch (error) {
    console.error("Error minting digital artifact:", error);
    throw error;
  }
}

/**
 * Get complete case details
 * @param {number} caseId - The ID of the case to check
 * @returns {Promise<Object>} The case details
 */
export async function getCaseDetails(caseId) {
  const contract = getContract();
  
  // Get all case details in parallel
  const [uri, status, plaintiff, defendant, owner] = await Promise.all([
    contract.tokenURI(caseId),
    contract.getCaseStatus(caseId),
    contract.getPlaintiff(caseId),
    contract.getDefendant(caseId),
    contract.ownerOf(caseId)
  ]);
  
  return {
    caseId,
    uri,
    status,
    plaintiff,
    defendant,
    owner
  };
}

/**
 * Get a block explorer URL for an address or transaction
 * @param {string} hash - The address or transaction hash
 * @param {string} type - The type of URL ('tx', 'address', or 'token')
 * @returns {string} The block explorer URL
 */
export function getExplorerUrl(hash, type = 'tx') {
  const baseUrl = BASE_MAINNET.blockExplorer;
  
  switch (type) {
    case 'tx':
      return `${baseUrl}/tx/${hash}`;
    case 'address':
      return `${baseUrl}/address/${hash}`;
    case 'token':
      // For tokens, we need to use our contract address and tokenId format
      return `${baseUrl}/token/${CONTRACT_ADDRESSES.registry}?a=${hash}`;
    default:
      return `${baseUrl}/${type}/${hash}`;
  }
}

/**
 * Get a token-specific explorer URL
 * @param {string} contractAddress - The token contract address (defaults to our contract if not provided)
 * @param {string} tokenId - The token ID
 * @returns {string} The block explorer URL for the token
 */
export function getTokenExplorerUrl(contractAddress = CONTRACT_ADDRESSES.registry, tokenId) {
  const baseUrl = BASE_MAINNET.blockExplorer;
  return `${baseUrl}/token/${contractAddress}?a=${tokenId}`;
}

/**
 * Switch the connected wallet to Base Mainnet
 * @returns {Promise<void>}
 */
export async function switchToBaseMainnet() {
  if (!window.ethereum) {
    throw new Error("MetaMask is not installed");
  }
  
  try {
    // Try to switch to Base network
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${BASE_MAINNET.chainId.toString(16)}` }], // 8453 in hex
    });
  } catch (switchError) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: `0x${BASE_MAINNET.chainId.toString(16)}`,
              chainName: BASE_MAINNET.name,
              nativeCurrency: {
                name: 'Ethereum',
                symbol: BASE_MAINNET.symbol,
                decimals: BASE_MAINNET.decimals,
              },
              rpcUrls: [BASE_MAINNET.rpcUrl],
              blockExplorerUrls: [BASE_MAINNET.blockExplorer],
            },
          ],
        });
      } catch (addError) {
        console.error('Error adding Base network:', addError);
        throw addError;
      }
    } else {
      console.error('Error switching to Base network:', switchError);
      throw switchError;
    }
  }
}

/**
 * Connect to the wallet and return account information
 * @returns {Promise<{address: string, provider: ethers.Provider, signer: ethers.Signer}>}
 */
export async function connectWallet() {
  if (!window.ethereum) {
    throw new Error("No Ethereum provider found. Please install MetaMask or another compatible wallet.");
  }
  
  try {
    // Request account access
    const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
    
    if (!accounts || accounts.length === 0) {
      throw new Error("No accounts found. Please make sure your wallet is unlocked.");
    }
    
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    
    // Get network information
    const network = await provider.getNetwork();
    const chainId = Number(network.chainId);
    
    // If not on Base Mainnet, prompt to switch
    if (chainId !== BASE_MAINNET.chainId) {
      await switchToBaseMainnet();
    }
    
    return {
      address: accounts[0],
      provider,
      signer,
      chainId: chainId
    };
  } catch (error) {
    console.error("Error connecting wallet:", error);
    throw error;
  }
}

/**
 * Format an ETH amount with specified decimal places and separator
 * @param {number|string} amount - The amount to format
 * @param {number} [decimals=4] - Number of decimal places to show
 * @param {boolean} [addSymbol=true] - Whether to add the ETH symbol
 * @returns {string} The formatted amount
 */
export function formatEthAmount(amount, decimals = 4, addSymbol = true) {
  if (!amount) return addSymbol ? '0 ETH' : '0';
  
  let value;
  if (typeof amount === 'string') {
    // If it's a BigNumber string, convert it
    if (amount.startsWith('0x')) {
      value = ethers.formatEther(amount);
    } else {
      value = parseFloat(amount);
    }
  } else {
    value = amount;
  }
  
  // Format the number with the specified decimal places
  const formattedNumber = parseFloat(value).toFixed(decimals);
  
  // Remove trailing zeros
  const cleanedNumber = formattedNumber.replace(/\.?0+$/, '');
  
  // Add the ETH symbol if requested
  return addSymbol ? `${cleanedNumber} ETH` : cleanedNumber;
}

// Create a named object for export to satisfy ESLint
const contractUtils = {
  BASE_MAINNET,
  CONTRACT_ADDRESSES,
  getProvider,
  getContract,
  hasRole,
  registerCase,
  updateCaseStatus,
  mintDigitalArtifact,
  getCaseDetails,
  getExplorerUrl,
  getTokenExplorerUrl,
  switchToBaseMainnet,
  formatAddress,
  getNetworkName,
  connectWallet,
  formatEthAmount
};

export default contractUtils;
